import { Typography } from '@mui/material';
import type { ElementType, ReactNode } from 'react';
import type { StructuredContent } from './hydration/types';

type ComponentProps = Record<string, Record<string, unknown>>;

export function createStructuredContentRenderer(
  components: Record<string, ElementType>,
  overrides?: Record<string, ElementType>,
) {
  function renderDoc(
    doc: StructuredContent | StructuredContent[],
    useLearnContentOverrides?: boolean,
    componentProps?: ComponentProps,
  ): ReactNode {
    if (typeof doc === 'string') {
      return <Typography component="span">{doc}</Typography>;
    }

    return (
      <>
        {[doc].flat().map((child, idx) => {
          if (typeof child === 'string') {
            return (
              <Typography key={`sc-doc-text-${idx}`} component="span">
                {child}
              </Typography>
            );
          }

          if (!child) return null;

          let Component = components[child.type];
          if (useLearnContentOverrides && overrides && child.type in overrides) {
            Component = overrides[child.type];
          }
          if (!Component) return null;

          const extraProps = componentProps?.[child.type];
          const mergedProps = extraProps
            ? { ...(child.props ?? {}), ...extraProps }
            : (child.props ?? {});

          return (
            <Component
              key={`sc-doc-${idx}`}
              type={child.type}
              version={child.version}
              props={mergedProps}
            />
          );
        })}
      </>
    );
  }

  function StructuredContentRenderer({
    doc,
    useLearnContentOverrides,
  }: {
    doc: StructuredContent;
    useLearnContentOverrides?: boolean;
  }) {
    return renderDoc(doc, useLearnContentOverrides);
  }

  return { StructuredContentRenderer, renderDoc };
}
